import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore } from '@/lib/core/stores';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { getEvents, useEventFavoriteIndex } from '@/services/platform/data/events';
import { EventCard } from '@/components/shared/events/list/EventCard';
import { isMembersOnly } from '@/components/shared/events/list/visibility';
import { isCardSoldOut } from '@/components/shared/events/rules';
import type { EventListItemDto } from '@/services/platform/api/model';
import { resolveHeadingLevel } from '../ui/heading-level';
import { MediaSlider } from '../ui/media-slider';
import type { LinkBlock } from './types';

/** Max events surfaced on the venue detail "What's On" carousel. */
const MAX_EVENTS = 6;

interface EventSliderBlockProps {
  title?: string | null;
  titleHeading?: string | null;
  link?: LinkBlock | null;
  /** Salesforce AL_Location id for the current venue — events are filtered to this address. */
  venueSalesforceId?: string | null;
}

export function EventSliderBlock({ title, titleHeading, link, venueSalesforceId }: EventSliderBlockProps) {
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const { user, appSettings } = useApp();
  const isMember = !!user?.hasActiveMembership;
  const eventDetailPath = appSettings?.properties?.eventDetail?.route?.path;
  const headingAs = resolveHeadingLevel(titleHeading);

  const [events, setEvents] = useState<EventListItemDto[]>([]);
  const {
    eventFavoriteLists,
    favoriteListGuidByEventId,
    refetch: refetchEventFavorites,
  } = useEventFavoriteIndex();

  useEffect(() => {
    if (!venueSalesforceId) return;
    let cancelled = false;
    // includeRestricted: false (default) — Members Only/Invite Only events are already
    // dropped server-side unless the caller can see them, so every card here is safe to open.
    // excludeExternal: true — external events (ticketed on partner sites) show only in the
    // calendar, not in the general explore listing.
    getEvents({ venueId: [venueSalesforceId], pageSize: MAX_EVENTS, excludeExternal: true }, locale).then(
      result => {
        if (!cancelled) setEvents(result?.items ?? []);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [venueSalesforceId, locale]);

  if (!title && !link?.url) return null;
  if (events.length === 0) return null;

  function handleSelect(event: EventListItemDto) {
    if (isCardSoldOut(event)) return;
    if (!isMember && isMembersOnly(event.visibility)) return;
    if (event.id && eventDetailPath) navigate(`${eventDetailPath}/${event.id}`);
  }

  return (
    <div className="flex flex-col gap-6 pl-4">
      <div className="flex items-center justify-between gap-4 pr-4">
        {title && (
          <Heading
            as={headingAs}
            className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[24px] leading-normal font-semibold"
          >
            {title}
          </Heading>
        )}
        {link?.url && (
          <Button
            asChild
            variant="tertiary-link"
            palette="dark"
            className="h-auto min-w-0 shrink-0 rounded-none px-0"
          >
            <a
              href={link.url}
              target={link.target ?? '_self'}
              rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
            >
              {link.name}
            </a>
          </Button>
        )}
      </div>

      <MediaSlider
        items={events}
        gapClassName="gap-4"
        gapPx={16}
        fallbackRatio={0.7}
        renderItem={(event, i, cardProps) => (
          <div key={event.id ?? i} className="w-[75vw] shrink-0 snap-start" {...cardProps}>
            <EventCard
              event={event}
              disabled={!isMember && isMembersOnly(event.visibility)}
              favoriteLists={eventFavoriteLists}
              refetchFavoriteLists={refetchEventFavorites}
              favoriteListGuid={event.id ? favoriteListGuidByEventId.get(event.id) : undefined}
              onRefetchFavoritesNeeded={refetchEventFavorites}
              onSelect={handleSelect}
            />
          </div>
        )}
      />
    </div>
  );
}
